import { FC, useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { toastr } from 'react-redux-toastr';
import { Exam } from 'store/exams-search/examsSearchAPI';
import { ExamCard as ExamCardBase } from 'components';
import { B2C_SUB_SYSTEM_SHORT_CODE, ServerErrorResponse, mapServerError, B2B_SUB_SYSTEM_SHORT_CODE } from 'core';
import { removeItem, selectBasketExams, setBasketItems } from 'store/basket/basketSlice';
import { selectExamsSearchPrerequisites, selectSubjectPrerequisites } from 'store/exams-search/examsSearchSlice';
import { useAppDispatch, useAppSelector } from 'store';
import { useLazyGetTeachingGroupQuery, useLazyPostBasketItemQuery } from 'store/basket/basketAPI';
import { selectPreparationCentre } from 'store/country/countrySlice';

import { Button } from '@britishcouncil/react-solas';

export const ExamCard: FC<{ exam: Exam }> = ({ exam }) => {
  const dispatch = useAppDispatch();
  const exams = useAppSelector(selectBasketExams);
  const subjectPrerequisites = useAppSelector(selectSubjectPrerequisites);
  const searchPrerequisites = useAppSelector(selectExamsSearchPrerequisites);

  const pcInfo = useAppSelector(selectPreparationCentre);

  const [addBasketItemQuery, { data, error, isFetching, isSuccess, originalArgs }] = useLazyPostBasketItemQuery({});

  const [getTeachingGroupQuery, teachingGroupQueryDetails] = useLazyGetTeachingGroupQuery();

  const basketItem = useMemo(() => exams.find((basketExam) => basketExam.examOptionId === exam.id), [exams, exam]);

  const [isRetake, setIsRetake] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (
      teachingGroupQueryDetails.data &&
      teachingGroupQueryDetails.isSuccess &&
      !teachingGroupQueryDetails.isFetching
    ) {
      let teachingGroupCode = teachingGroupQueryDetails.data[0];
      invokeAddBasketQuery(isRetake, teachingGroupCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teachingGroupQueryDetails]);

  useEffect(() => {
    if (data && isSuccess && !isFetching) {
      dispatch(setBasketItems(data));
    }
  }, [data, isSuccess, isFetching, dispatch]);

  useEffect(() => {
    if (error) {
      const { status } = error as ServerErrorResponse;
      if (status === 400) {
        const serverErrors = mapServerError(error);
        toastr.warning('Could not add exam to cart', serverErrors[0]?.message ?? '');
      }
    }
  }, [error]);

  return (
    <ExamCardBase exam={exam}>
      {!basketItem && (
        <StyledButton size="xs" intent={'success'} onClick={() => handleItemAdd()} disabled={isFetching}>
          {isFetching && !originalArgs?.retakeSelected ? 'Adding...' : 'Add to basket'}
        </StyledButton>
      )}

      {exam.retakeAvailable && (!basketItem || basketItem.retakeSelected) && (
        <>
          <StyledRetakeDivider />
          <StyledRetakeFeesContainer>
            {exam.retakeFee && (
              <RetakeFee label="Retake fee:" value={`${exam.retakeFee.toFixed(2)} ${exam.currency}`} />
            )}
          </StyledRetakeFeesContainer>
          {!basketItem && (
            <StyledButton
              size="xs"
              intent={'default'}
              onClick={() => handleItemAdd(true)}
              disabled={isFetching || teachingGroupQueryDetails.isFetching}
            >
              {isFetching && originalArgs?.retakeSelected ? 'Adding...' : 'Add with retake'}
            </StyledButton>
          )}
        </>
      )}
      {basketItem && (
        <StyledButton size="xs" intent={'warning'} onClick={() => dispatch(removeItem(exam.id))}>
          Remove from basket
        </StyledButton>
      )}
    </ExamCardBase>
  );

  function handleItemAdd(retake?: boolean) {
    if (subjectPrerequisites.session!.isCheckpoint === true) {
      setIsRetake(retake);

      getTeachingGroupQuery({
        examSyllabusCode: exam.syllabusCode,
        productGroupSubSystemId: +subjectPrerequisites.productSubSystemId,
        sessionId: +subjectPrerequisites.session!.sessionId,
      });
    } else {
      invokeAddBasketQuery(retake);
    }
  }

  function invokeAddBasketQuery(retake?: boolean, teachingGroup?: string) {
    addBasketItemQuery({
      basketExamOptions: exams,
      addedExamOptionId: exam.id,
      awardingBodyId: Number(subjectPrerequisites.awardingBody),
      districtId: Number(subjectPrerequisites.location),
      originSubSystemShortCode: B2C_SUB_SYSTEM_SHORT_CODE,
      sessionId: Number(subjectPrerequisites.session?.sessionId),
      subSystemShortCode: pcInfo ? B2B_SUB_SYSTEM_SHORT_CODE : B2C_SUB_SYSTEM_SHORT_CODE,
      retakeSelected: !!retake,
      teachingGroup: teachingGroup,
      preparationCentreCode: searchPrerequisites.preparationCentreCode,
    });
  }
};

const RetakeFee: FC<{ label: string; value: string | number | undefined }> = ({ label, value }) => (
  <StyledRetakeFeeContainer>
    <span>{label}</span>
    <strong>{value}</strong>
  </StyledRetakeFeeContainer>
);

const StyledButton = styled(Button)`
  margin-top: 1em;
`;

const StyledRetakeDivider = styled.hr`
  border-top: 1px solid #8f9fad;
  margin: 16px 0;
`;

const StyledRetakeFeesContainer = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  grid-row-gap: 4px;
  padding: 2px 0;
`;

const StyledRetakeFeeContainer = styled.div`
  display: contents;
`;
